import { formatNumber } from 'util/formatNumber';
import { STATES } from 'util/types/states';
import { Box, LinearProgress, Typography } from '@mui/material';
import { ImageIcon, ImagePaperIcon, MinusBoxIcon } from 'components/icons';
import { Link, Image } from 'components/lib';
import {
  AddLink,
  Button,
  ButtonLink,
  FormErrors,
  Surface,
  TextInput,
} from 'components/ui';
import { Select } from 'components/ui/Select/Select';
import {
  useAppForm,
  useIsMasterAccount,
  useProfile,
  useRequireRole,
} from 'hooks';
import { useImageUpload } from 'hooks/useImageUpload';
import { MasterPanelPageLayout } from 'layouts/pages/master-panel';
import { MAX_IMAGE_FILE_SIZE_MB } from 'pages/admin/details';
import React, { ReactElement, useEffect, useState } from 'react';
import { Routes } from 'services/routes';
import { Controller, useFieldArray, useWatch } from 'react-hook-form';
import {
  CreateSchoolForm,
  createSchoolFormSchema,
} from 'forms/master/create-school.form';
import { useSchoolsApi } from 'services/api';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { ErrorMessages } from 'forms/error-messages';
import { HideSchoolSwitch } from 'components/pages/master';
import useFeature, { FeatureFlag } from 'hooks/useFeature';

const MAX_LOGO_FILE_SIZE_MB = 5;
const MAX_BANNER_FILE_SIZE_MB = 10;

const FormField = ({
  register,
  name,
  label,
  defaultValue,
  error,
  autoComplete = 'off',
  minWidth = 262,
}: {
  register: any;
  name: string;
  label: string;
  defaultValue?: string;
  error?: string;
  autoComplete?: string;
  minWidth?: number;
}) => (
  <Box minWidth={minWidth}>
    <TextInput
      {...register(name)}
      autoComplete={autoComplete}
      label={label}
      defaultValue={defaultValue}
    />
    {error && <FormErrors>{error}</FormErrors>}
  </Box>
);

const ImageUploadSection = ({
  title,
  imageUrl,
  onOpenSelector,
  uploadProgress,
  maxFileSize,
  recommendedSize,
  width = 70,
  height = 70,
  style,
}: {
  title: string;
  imageUrl: string | null;
  onOpenSelector: () => void;
  uploadProgress?: number;
  maxFileSize: number;
  recommendedSize: string;
  width?: number | string;
  height?: number;
  style?: React.CSSProperties;
}) => (
  <Surface mt={40} mr={18} noInnerPadding position="relative" overflow="hidden">
    <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
      {title}
    </Typography>
    <Box mt={6} mb={5} display="flex">
      <Box mt={6} flex={1}>
        <Link onClick={onOpenSelector}>
          <Box
            display="flex"
            alignItems="center"
            tabIndex={0}
            className="keyboardFocus"
            onKeyPress={e => e.key === ' ' && onOpenSelector()}
          >
            <Box mr={10} lineHeight={0}>
              <ImagePaperIcon />
            </Box>
            <Typography sx={{ fontWeight: 600 }}>
              Click Here To Upload Image
            </Typography>
          </Box>
        </Link>
        <Box mt={6}>
          <Typography sx={{ fontSize: '0.875em' }}>
            JPEG or PNG. File size no larger than {maxFileSize}MB.
            <br />
            Recommended {recommendedSize}.
          </Typography>
        </Box>
      </Box>
      <Box
        mt={-20}
        flex={1}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {imageUrl ? (
          <Box
            lineHeight={0}
            style={{
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
            }}
            onClick={onOpenSelector}
          >
            <Image
              src={imageUrl}
              width={width}
              height={height}
              alt="Uploaded Image"
              style={{ objectFit: 'cover', ...style }}
            />
          </Box>
        ) : (
          <Box
            py={10}
            px={10}
            lineHeight={0}
            style={{
              background: '#efefef',
              color: '#d5d5d5',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0,0,0,0)',
            }}
            onClick={onOpenSelector}
          >
            <ImageIcon style={{ fontSize: 52 }} />
          </Box>
        )}
      </Box>
    </Box>
    {uploadProgress && (
      <LinearProgress
        variant="determinate"
        value={uploadProgress}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 8,
        }}
      />
    )}
  </Surface>
);

const BannerImageSection = ({
  bannerUrl,
  onOpenSelector,
  uploadProgress,
}: {
  bannerUrl: string | null;
  onOpenSelector: () => void;
  uploadProgress?: number;
}) => (
  <Surface mt={40} mr={18} noInnerPadding position="relative" overflow="hidden">
    <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
      School Banner Image
    </Typography>

    <Box mt={12}>
      <Link onClick={onOpenSelector}>
        <Box
          display="flex"
          alignItems="center"
          tabIndex={0}
          className="keyboardFocus"
          onKeyPress={e => e.key === ' ' && onOpenSelector()}
        >
          <Box mr={10} lineHeight={0}>
            <ImagePaperIcon />
          </Box>
          <Typography sx={{ fontWeight: 600 }}>
            Click Here To Upload Image
          </Typography>
        </Box>
      </Link>
    </Box>

    <Box
      mt={12}
      maxWidth={625}
      tabIndex={0}
      onClick={onOpenSelector}
      onKeyPress={e => {
        if (e.key === ' ' || e.key === 'Enter') {
          onOpenSelector();
          e.preventDefault();
        }
      }}
      className="keyboardFocus"
      lineHeight={0}
    >
      {bannerUrl ? (
        <Image
          src={bannerUrl}
          alt="School Banner"
          width={625}
          height={250}
          style={{ objectFit: 'cover', cursor: 'pointer' }}
        />
      ) : (
        <Box
          height={250}
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          lineHeight={0}
          style={{
            background: '#efefef',
            color: '#d5d5d5',
            cursor: 'pointer',
          }}
        >
          <ImageIcon style={{ fontSize: 80 }} />
          <Typography
            color="primary"
            style={{
              fontSize: '0.875em',
              fontWeight: 600,
            }}
          >
            Click Here To Upload Image
          </Typography>
          <Typography
            color="textPrimary"
            align="center"
            style={{
              fontSize: '0.875em',
            }}
          >
            JPEG or PNG. File size no larger than {MAX_BANNER_FILE_SIZE_MB} MB.
            Recommended {formatNumber(1250)} x {formatNumber(500)} pixels.
          </Typography>
        </Box>
      )}
    </Box>

    {uploadProgress && (
      <LinearProgress
        variant="determinate"
        value={uploadProgress}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 8,
        }}
      />
    )}
  </Surface>
);

const AliasField = ({
  field,
  index,
  register,
  onRemove,
  error,
  getValues,
}: {
  field: any;
  index: number;
  register: any;
  onRemove: () => void;
  error?: string;
  getValues: any;
}) => (
  <React.Fragment key={field.id}>
    <Box
      mt={index === 0 ? 20 : 0}
      width={300}
      display="flex"
      alignItems="flex-end"
    >
      <Box
        mb={16}
        pr={10}
        lineHeight={0}
        style={{ cursor: 'pointer' }}
        onClick={onRemove}
      >
        <MinusBoxIcon color="error" style={{ fontSize: '1.125em' }} />
      </Box>
      <TextInput
        label={index === 0 ? 'School Alias' : undefined}
        autoComplete="off"
        defaultValue={getValues(`aliases.${index}.value`)}
        {...register(`aliases.${index}.value`)}
      />
    </Box>
    <Box pb={20}>
      {error && (
        <Box pl={30}>
          <FormErrors>{error}</FormErrors>
        </Box>
      )}
    </Box>
  </React.Fragment>
);

const useImageValidation = (enqueueSnackbar: any) => {
  const validateImages = (
    iconUrl: string | null,
    logoUrl: string | null,
    bannerUrl: string | null,
  ) => {
    if (!iconUrl) {
      enqueueSnackbar('Please upload an icon', { variant: 'error' });
      return false;
    }
    if (!logoUrl) {
      enqueueSnackbar('Please upload a logo', { variant: 'error' });
      return false;
    }
    if (!bannerUrl) {
      enqueueSnackbar('Please upload a banner', { variant: 'error' });
      return false;
    }
    return true;
  };

  return { validateImages };
};

export default function MasterPanelCreateSchoolPage(): ReactElement {
  const { loading } = useProfile();
  const isMaster = useIsMasterAccount();
  useRequireRole('master');

  const schoolsApi = useSchoolsApi();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { validateImages } = useImageValidation(enqueueSnackbar);
  const {
    formState: { errors },
    ...form
  } = useAppForm(createSchoolFormSchema, {
    defaultValues: {
      aliases: [],
    },
    mode: 'all',
  });

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);

  const logoUpload = useImageUpload({ maxMb: MAX_LOGO_FILE_SIZE_MB });
  const iconUpload = useImageUpload({ maxMb: MAX_LOGO_FILE_SIZE_MB });
  const bannerUpload = useImageUpload({ maxMb: MAX_BANNER_FILE_SIZE_MB });

  useEffect(() => {
    setIconUrl(iconUpload.url);
  }, [iconUpload.url]);

  useEffect(() => {
    setLogoUrl(logoUpload.url);
  }, [logoUpload.url]);

  useEffect(() => {
    setBannerUrl(bannerUpload.url);
  }, [bannerUpload.url]);

  const save = async (data: CreateSchoolForm) => {
    if (!validateImages(iconUrl, logoUrl, bannerUrl)) return;

    const result = await schoolsApi.createSchool({
      ...data,
      iconUrl: iconUrl ?? '',
      logoUrl: logoUrl ?? '',
      bannerUrl: bannerUrl ?? '',
      aliases: data.aliases?.map(({ value }) => value) ?? [],
    });

    if (!result.success) {
      enqueueSnackbar(result.error.response?.data.message, {
        variant: 'error',
      });
      return;
    }

    const id = result.value.id;

    enqueueSnackbar('Successfully created school', { variant: 'success' });
    await router.push(Routes.master.school(id));
  };

  const aliases = useFieldArray({
    control: form.control,
    name: 'aliases',
  });

  useWatch({
    control: form.control,
    name: 'aliases',
  });

  const state = useWatch({
    control: form.control,
    name: 'state',
  });

  const stateSlug = (state || 'state').toLowerCase().replace(/\s+/g, '-');

  if (loading || !isMaster) {
    return (
      <MasterPanelPageLayout title="Create School" active="schools">
        {''}
      </MasterPanelPageLayout>
    );
  }

  return (
    <MasterPanelPageLayout
      title="Create School"
      active="schools"
      actions={
        <Controller
          control={form.control}
          name="isHidden"
          render={({ field: { value, onChange } }) => (
            <HideSchoolSwitch
              isHidden={!!value}
              onChange={() => onChange(!value)}
            />
          )}
        />
      }
    >
      <form onSubmit={form.handleSubmit(save)}>
        <Box maxWidth={892}>
          <Surface mt={20} mr={18} noInnerPadding>
            <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
              School Information
            </Typography>
            <Box
              my={20}
              display="flex"
              flexWrap="wrap"
              style={{ rowGap: 40, columnGap: 50 }}
            >
              <FormField
                register={form.register}
                name="name"
                label="School Name"
                error={errors.name?.message}
              />
              <FormField
                register={form.register}
                name="websiteUrl"
                label="School Website"
                error={errors.websiteUrl?.message}
              />
              <FormField
                register={form.register}
                name="streetAddress"
                label="Street Address"
                error={errors.streetAddress?.message}
              />
              <FormField
                register={form.register}
                name="city"
                label="City"
                error={errors.city?.message}
              />
              <Box minWidth={262}>
                <Select {...form.register('state')} label="State">
                  <option></option>
                  {
                    STATES.map(state => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    )) as any
                  }
                </Select>
                {errors.state?.message && (
                  <FormErrors>{ErrorMessages.Required}</FormErrors>
                )}
              </Box>
              <FormField
                register={form.register}
                name="zipCode"
                label="Zip Code"
                error={errors.zipCode?.message}
              />
              <FormField
                register={form.register}
                name="termsUrl"
                label="T&C URL"
                error={errors.termsUrl?.message}
              />
              <FormField
                register={form.register}
                name="termsDisplayName"
                label="T&C Display Name"
                error={errors.termsDisplayName?.message}
              />
            </Box>
          </Surface>

          <Surface mt={40} mr={18} noInnerPadding>
            <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
              School Email Domain
            </Typography>
            <Typography sx={{ fontSize: '0.875em' }}>
              Users who register with emails from this domain will be associated
              with this school
            </Typography>
            <Box maxWidth={300}>
              <TextInput {...form.register('domainName')} />
            </Box>
            {errors.domainName?.message && (
              <FormErrors>{errors.domainName?.message}</FormErrors>
            )}
          </Surface>

          <Surface mt={40} mr={18} noInnerPadding>
            <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
              Alternative School Names
            </Typography>
            {aliases.fields.map((field, i: number) => (
              <AliasField
                key={field.id}
                field={field}
                index={i}
                register={form.register}
                onRemove={() => aliases.remove(i)}
                error={errors?.aliases?.[i]?.value?.message}
                getValues={form.getValues}
              />
            ))}
            <Box mt={10}>
              <AddLink onClick={() => aliases.append({ value: '' })}>
                Add Alias
              </AddLink>
            </Box>
          </Surface>

          <Surface mt={40} mr={18} noInnerPadding>
            <Typography sx={{ fontSize: '1.125em', fontWeight: 600 }}>
              Custom CampusLush URL Slug
            </Typography>
            <Box my={10} display="flex" alignItems="center" style={{ gap: 2 }}>
              <Typography>
                {window.location.host}/{stateSlug}/
              </Typography>
              <TextInput {...form.register('slug')} />
            </Box>
          </Surface>

          <ImageUploadSection
            title="School Icon"
            imageUrl={iconUrl}
            onOpenSelector={iconUpload.open}
            uploadProgress={iconUpload.progress ?? 0}
            maxFileSize={MAX_IMAGE_FILE_SIZE_MB}
            recommendedSize="200 x 200 pixels"
          />

          <ImageUploadSection
            title="School Logo"
            imageUrl={logoUrl}
            onOpenSelector={logoUpload.open}
            uploadProgress={logoUpload.progress ?? 0}
            maxFileSize={MAX_IMAGE_FILE_SIZE_MB}
            recommendedSize="340 x 120 pixels"
            width="100%"
            height={50}
          />

          <BannerImageSection
            bannerUrl={bannerUrl}
            onOpenSelector={bannerUpload.open}
            uploadProgress={bannerUpload.progress ?? 0}
          />

          <Box mt={40} display="flex">
            <Box mr={30}>
              <ButtonLink
                inverted
                minHeight={50}
                href={Routes.master.schools()}
              >
                Back
              </ButtonLink>
            </Box>
            <Button
              variant="vibrant"
              minHeight={50}
              type="submit"
              disabled={!logoUrl || !bannerUrl || !iconUrl}
            >
              Save School
            </Button>
          </Box>
        </Box>
      </form>
    </MasterPanelPageLayout>
  );
}
